import { BasicSelect, EmptyBlockContent } from '@tradingblock/components';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Cell } from 'react-table';
import { throttle } from 'lodash';
import YearPicker from '../../../components/basic/YearPicker';
import { useApi } from '../../../context/Api';
import { AccountStatementsActions } from '../../../data/global/actions/AccountStatementsActions';
import { useStateSelector } from '../../../data/global/dataSelectors';
import { BlockTable } from '../../shared/BlockTable';
import { SessionActions } from '../../../data/global/actions';
import { GenericErrorResponseCode } from '@tradingblock/types';
import { DownloadComponent } from '../components/DownloadComponent';

export interface AccountStatementsDailyConfirmsProps {}

export default function AccountStatementsTaxDocuments() {
  const dispatch = useDispatch();
  const api = useApi();
  const accountId = useStateSelector(s => s.account.accountId);
  const selectedAccount = useStateSelector(s => s.account.selectedLinkedAccount);
  const [year, setYear] = useState(new Date().getFullYear() - 1);
  const timeframe = useMemo(
    () => ({
      startDate: new Date(year, 0, 1),
      endDate: new Date(year, 11, 31),
    }),
    [year]
  );
  const [taxDocument, setTaxDocument] = useState<
    'All' | '1099' | '1099R' | '5498' | '5498ESA' | '1042S' | '2439' | '408'
  >('All');

  const { data: taxDocuments, isLoading, error } = useStateSelector(state => state.accountStatements.taxStatements);

  const formattedTaxDocuments = useMemo(() => {
    if (taxDocuments) {
      return taxDocuments
        .sort((a, b) => (new Date(a.date) < new Date(b.date) ? 1 : -1))
        .map(document => ({
          received_on: new Date(document.date),
          message: `${document.form} for ${dayjs(document.date).format('MM/DD/YY')}`,
          documentType: document.form,
        }));
    } else {
      return [];
    }
  }, [taxDocument, taxDocuments, year]);

  const requestTaxAccountStatementsThrottleCallback = useCallback(
    throttle((year, taxDocument, accountId) => {
      dispatch(
        AccountStatementsActions.requestTaxAccountStatements({
          accountId: selectedAccount ? selectedAccount.accountId : accountId,
          taxDocument: taxDocument,
          taxYear: year,
        })
      );
    }, 100),
    [dispatch, selectedAccount, accountId]
  );

  useEffect(() => {
    requestTaxAccountStatementsThrottleCallback(year, taxDocument, accountId);
  }, [year, taxDocument, accountId]);

  const columns = useMemo(
    () => [
      {
        Header: 'Statement',
        canSort: false,
        accessor: (a: any) => a.description,
        id: 'message',
        Cell: ({ row }: Cell<any>) => row.original.message,
      },
      {
        Header: 'View',
        accessor: (a: any) => dayjs(a.received).format('MM/DD/YY h:mm A'),
        id: 'received_on',
        canSort: true,
        sortType: 'basic',
        Cell: ({
          row,
        }: Cell<{
          received_on: Date;
          message: string;
          documentType: string;
        }>) => (
          <i
            style={{
              cursor: 'pointer',
            }}
            className="fal fa-lg fa-search dashboard-icon"
            onClick={() => {
              const [_, formType] = row.original.documentType.split('Form');
              api.downloads
                .getTaxDocument(accountId, formType, row.original.received_on)
                .then((result: any) => {
                  const file = new Blob([result.data], { type: 'application/pdf' });
                  const fileURL = URL.createObjectURL(file);
                  window.open(fileURL);
                })
                .catch((err: { config: { url: string | undefined } }) => {
                  dispatch(
                    SessionActions.error({
                      code: GenericErrorResponseCode,
                      url: err && err.config && err.config.url ? err.config.url : '',
                      message: 'Error downloading tax document. Please try again later.',
                      reason: 'Download Failed',
                    })
                  );
                });
            }}
          />
        ),
      },
      {
        Header: 'Download',
        accessor: (a: any) => dayjs(a.received).format('MM/DD/YY h:mm A'),
        id: 'download',
        canSort: true,
        sortType: 'basic',
        Cell: ({
          row,
        }: Cell<{
          received_on: Date;
          message: string;
          documentType: string;
        }>) => (
          <DownloadComponent
            row={row}
            accountId={accountId}
            api={api}
            dispatch={dispatch}
            SessionActions={SessionActions}
            GenericErrorResponseCode={GenericErrorResponseCode}
            type="taxDoc"
          />
        ),
      },
    ],
    [accountId, taxDocument]
  );

  return (
    <>
      <div className="block-section block-section-fixed">
        <div className="row justify-content-between">
          <div className="col-auto">
            <YearPicker
              value={year}
              onChange={newYear => {
                setYear(newYear);
              }}
              disabled={isLoading}
            />
          </div>
          <div className="col-auto">
            <span>Tax Document Selector: </span>
            <BasicSelect
              className="btn btn-dark btn-tall dropdown-toggle"
              options={[
                {
                  value: 'All',
                  label: 'All',
                },
                {
                  value: '1099',
                  label: '1099',
                },
                {
                  value: '1099R',
                  label: '1099R',
                },
                {
                  value: '5498',
                  label: '5498',
                },
                {
                  value: '5498ESA',
                  label: '5498ESA',
                },
                {
                  value: '1042S',
                  label: '1042S',
                },
                {
                  value: '2439',
                  label: '2439',
                },
                {
                  value: '408',
                  label: '408',
                },
              ]}
              onChange={(selectedTaxDocument: { value: string; label: string }) => {
                setTaxDocument(selectedTaxDocument.value as typeof taxDocument);
              }}
              placeholder={taxDocument}
              value={taxDocument}
              isDisabled={isLoading}
            />
          </div>
        </div>
      </div>
      {!isLoading && !error && formattedTaxDocuments.length > 0 && (
        <BlockTable
          tableKey="orders"
          columnWidths={['80%', '10%', '10%']}
          columns={columns}
          data={formattedTaxDocuments}
          sortable
          frozenHeaders
          loaded={!isLoading}
        />
      )}
      {!isLoading && !error && formattedTaxDocuments.length === 0 && (
        <EmptyBlockContent>
          <p>No tax documents found</p>
        </EmptyBlockContent>
      )}
      {isLoading && (
        <>
          <i className="fas fa-spinner fa-spin" /> Loading tax documents...
        </>
      )}
      {error && (
        <EmptyBlockContent>
          <div className="text-danger">
            <i className="fas fa-exclamation-triangle" /> Error loading tax documents:{' '}
            {error.message || 'Unknown error'}
          </div>
        </EmptyBlockContent>
      )}
    </>
  );
}
